import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "global-notice"
}
const _hoisted_2 = { class: "notice-banner" }
const _hoisted_3 = { class: "notice-icon" }
const _hoisted_4 = { class: "notice-meta" }
const _hoisted_5 = ["innerHTML"]

import { onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Bell } from '@element-plus/icons-vue'
import { getNotice, listNoticeTop, markNoticeRead } from '@/api/notice'
import type { EntityId, NoticeItem } from '@/types/api'
import { getToken, getUser } from '@/utils/auth'


export default /*@__PURE__*/_defineComponent({
  __name: 'GlobalNoticePopup',
  setup(__props) {

const route = useRoute()
const visible = ref(false)
const detailLoading = ref(false)
const currentNotice = ref<NoticeItem | null>(null)
const noticeQueue: NoticeItem[] = []
const displayedNoticeIds = new Set<EntityId>()
let checking = false
let closedTimer: number | undefined
let currentUserKey = ''
let detailRequestId = 0

function getNoticeTypeLabel(noticeType?: string): string {
  return noticeType === '1' ? '通知' : '公告'
}

function isPending(noticeId: EntityId): boolean {
  return currentNotice.value?.noticeId === noticeId
    || noticeQueue.some((notice) => notice.noticeId === noticeId)
}

function showNextNotice() {
  if (visible.value || !noticeQueue.length) return

  const notice = noticeQueue.shift()
  if (!notice) return
  currentNotice.value = notice
  visible.value = true
  detailLoading.value = true
  const requestId = ++detailRequestId

  getNotice(notice.noticeId)
    .then((response) => {
      if (requestId === detailRequestId && currentNotice.value?.noticeId === notice.noticeId) {
        currentNotice.value = response?.data || notice
      }
    })
    .catch(() => {
      currentNotice.value = currentNotice.value || notice
    })
    .finally(() => {
      if (requestId === detailRequestId) {
        detailLoading.value = false
      }
    })

  markNoticeRead(notice.noticeId).then(() => {
    window.dispatchEvent(new CustomEvent('ruoyi-notice-read', {
      detail: { noticeId: notice.noticeId }
    }))
  }).catch(() => undefined)
}

async function checkNewNotices() {
  if (!getToken() || checking) return

  const user = getUser()
  const userKey = String(user?.userId || user?.id || user?.username || '')

  if (currentUserKey !== userKey) {
    currentUserKey = userKey
    displayedNoticeIds.clear()
    noticeQueue.splice(0)
  }

  checking = true
  try {
    const response = await listNoticeTop()
    const notices = (response?.data || [])
      .filter((notice) => !notice.isRead && String(notice.status ?? '0') === '0')
      .filter((notice) => !displayedNoticeIds.has(notice.noticeId) && !isPending(notice.noticeId))
      .sort((left, right) => Number(left.noticeId) - Number(right.noticeId))

    notices.forEach((notice) => {
      displayedNoticeIds.add(notice.noticeId)
      noticeQueue.push(notice)
    })
    showNextNotice()
  } catch {
    return
  } finally {
    checking = false
  }
}

function handleClosed() {
  detailRequestId += 1
  detailLoading.value = false
  currentNotice.value = null
  if (closedTimer !== undefined) window.clearTimeout(closedTimer)
  closedTimer = window.setTimeout(showNextNotice, 120)
}

const stopRouteWatch = watch(
  () => route.fullPath,
  () => checkNewNotices().catch(() => undefined),
  { immediate: true }
)

onBeforeUnmount(() => {
  stopRouteWatch()
  if (closedTimer !== undefined) window.clearTimeout(closedTimer)
})

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    modelValue: visible.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((visible).value = $event)),
    "append-to-body": "",
    "close-on-click-modal": false,
    title: currentNotice.value?.noticeTitle || '新公告通知',
    width: "680px",
    onClosed: handleClosed
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_el_button, {
        type: "primary",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (visible.value = false))
      }, {
        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
          _createTextVNode("我知道了", -1)
        ]))]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      (currentNotice.value)
        ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("span", _hoisted_3, [
                _createVNode(_component_el_icon, null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Bell))
                  ]),
                  _: 1
                })
              ]),
              _createElementVNode("div", null, [
                _cache[2] || (_cache[2] = _createElementVNode("span", null, "NEW NOTICE", -1)),
                _createElementVNode("strong", null, "您收到一条新的" + _toDisplayString(getNoticeTypeLabel(currentNotice.value.noticeType)), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_el_tag, {
                type: currentNotice.value.noticeType === '1' ? 'warning' : 'success',
                effect: "plain"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(getNoticeTypeLabel(currentNotice.value.noticeType)), 1)
                ]),
                _: 1
              }, 8, ["type"]),
              _createElementVNode("span", null, _toDisplayString(currentNotice.value.createBy || '系统管理员'), 1),
              _createElementVNode("span", null, _toDisplayString(currentNotice.value.createTime || ''), 1)
            ]),
            _createElementVNode("div", {
              class: "notice-content",
              innerHTML: currentNotice.value.noticeContent || '暂无公告内容'
            }, null, 8, _hoisted_5)
          ])), [
            [_directive_loading, detailLoading.value]
          ])
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})